'use client'

import React from 'react'
import dynamic from 'next/dynamic'
import type { RenderedService } from '@/types/interfaces/service-item'
import { RenderedServicesType, ServiceItemCardType } from '@/types/enums/ServiceItem'
import type { CategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import { getTestimonialReviewsV2Data } from '@/lib/hardcoded-data/CategoryPages/testimonials'
import { PageLocation } from '@nile/mantine-theme/analytics'

// Home above-fold: keep eager so LCP/first paint JS stays hot.
import FeaturedCategoryRow from '@/components/Refresh/StoreHome/FeaturedCategory'
import InsuranceAccepted from '@/components/Refresh/StoreHome/InsuranceAccepted'
import CategoryDirectory from '@/components/Refresh/StoreHome/CategoryDirectory'
import ShelfGroup from '@/components/Refresh/StoreHome/ShelfGroup'
import CategoryHero from '@/components/Refresh/StoreHome/CategoryHero'
import CategoryPageTextHeader from './CategoryPageTextHeader'

// Category / secondary store sections: SSR markup stays; JS chunks load off the home critical path.
const CategoryHeader = dynamic(() => import('./CategoryHeader'))
const CategoryHeaderWithSearch = dynamic(() => import('./CategoryHeaderWithSearch'))
const TabbedCardSliderV2 = dynamic(() => import('./TabbedCardSliderV2'))
const TabbedCardSlider = dynamic(() => import('@/components/Refresh/TabbedCardSlider/TabbedCardSlider'))
const CarouselV2 = dynamic(() => import('./CarouselV2'))
const StoreSkuCarousel = dynamic(() => import('@/components/Refresh/StoreSkuCarousel/StoreSkuCarousel'))
const StoreStack = dynamic(() => import('@/components/Store/StoreStack'))
const SpecialistCard = dynamic(() => import('@/components/SpecialistCard'))
const FeaturedService = dynamic(() => import('@/components/Refresh/FeaturedService/FeaturedService'))
const StoreCategoryGrid = dynamic(() => import('@/components/Store/StoreCategoryGrid'))
const CategorySearch = dynamic(() => import('./CategorySearch'))
const SupportWidget = dynamic(() => import('@/components/Refresh/SupportWidget/SupportWidget'))
const AISection = dynamic(() => import('@/components/Refresh/AIWidget/AISection'))
const ClinitianWidget = dynamic(() => import('@/components/Refresh/ClinitianWidget/ClinitianWidget'))
const TestimonialsWidget = dynamic(() => import('@/components/Refresh/TestimonialsWidget/TestimonialsWidget'))
const BlogSlider = dynamic(() => import('@/components/Refresh/BlogWidget/BlogSlider'))
const WidgetContentAccordion = dynamic(() => import('./WidgetContentAccordion'))
const CategoryContentWithBulletList = dynamic(() => import('./CategoryContentWithBulletList'))
const WidgetGenAi = dynamic(() => import('./WidgetGenAi'))
const WidgetMultiVideoPlayer = dynamic(() => import('./WidgetMultiVideoPlayer'))
const WidgetTestimonialReviewsV2 = dynamic(() => import('./WidgetTestimonialReviewsV2'))
const WidgetTestimonialUGC = dynamic(() => import('./WidgetTestimonialUGC'))
const WidgetGetStartedCta = dynamic(() => import('./WidgetGetStartedCta'))
const WidgetFAQ = dynamic(() => import('./WidgetFAQ'))
const WidgetHowItWorks = dynamic(() => import('./WidgetHowItWorks'))
const WidgetCategoryStats = dynamic(() => import('./WidgetCategoryStats'))

export type CommonStoreProps = {
    pageLocation: PageLocation
    additionalInformation: { categoryName: string }
}

type RenderPredicate = (service: RenderedService, categoryName: string) => boolean

export interface RegistryEntry {
    type: RenderedServicesType
    cardType?: ServiceItemCardType
    predicate?: RenderPredicate
    dataKey?: keyof CategoryPageData
    component: React.ComponentType<any>
}

const hasTabbedServices = (s: RenderedService): boolean =>
    'tabbedServices' in s && !!s.tabbedServices?.length && !s.tabs?.length

const hasWholeBodyCarousel = (_s: RenderedService, categoryName: string): boolean =>
    !!getCategoryPageData(categoryName, 'wholeBodyCarousel')

/** Data-driven: match when category has hardcoded data for this widget. */
const hasCategoryData =
    (dataKey: keyof CategoryPageData) =>
    (_s: RenderedService, categoryName: string): boolean =>
        !!getCategoryPageData(categoryName, dataKey)

/**
 * Unified registry: maps (type, cardType?, predicate) to component.
 * Order matters: first match wins.
 */
export const COMPONENT_REGISTRY: RegistryEntry[] = [
    {
        type: RenderedServicesType.categoryHeaderWithSearch,
        dataKey: 'categoryHeaderWithSearch',
        component: CategoryHeaderWithSearch,
    },
    {
        type: RenderedServicesType.categoryHeader,
        dataKey: 'categoryHeaderWithSearch',
        predicate: hasCategoryData('categoryHeaderWithSearch'),
        component: CategoryHeaderWithSearch,
    },
    {
        type: RenderedServicesType.categoryHeader,
        dataKey: 'categoryHeader',
        component: CategoryHeader,
    },

    {
        type: RenderedServicesType.categorySearch,
        dataKey: 'categorySearch',
        component: CategorySearch,
    },
    {
        type: RenderedServicesType.howItWorks,
        dataKey: 'howItWorks',
        component: WidgetHowItWorks,
    },
    {
        type: RenderedServicesType.contentWithBulletList,
        dataKey: 'contentWithBulletList',
        component: CategoryContentWithBulletList,
    },
    {
        type: RenderedServicesType.contentAccordion,
        dataKey: 'contentAccordion',
        predicate: hasCategoryData('contentAccordion'),
        component: WidgetContentAccordion,
    },
    {
        type: RenderedServicesType.generalAISm,
        predicate: hasCategoryData('generalAISm'),
        component: WidgetGenAi,
    },
    {
        type: RenderedServicesType.categoryStats,
        dataKey: 'categoryStats',
        predicate: hasCategoryData('categoryStats'),
        component: WidgetCategoryStats,
    },
    {
        type: RenderedServicesType.testimonialUgc,
        dataKey: 'testimonialUgc',
        predicate: hasCategoryData('testimonialUgc'),
        component: WidgetTestimonialUGC,
    },
    {
        type: RenderedServicesType.getStartedCta,
        dataKey: 'getStartedCta',
        predicate: hasCategoryData('getStartedCta'),
        component: WidgetGetStartedCta,
    },
    {
        type: RenderedServicesType.faq,
        dataKey: 'faq',
        predicate: hasCategoryData('faq'),
        component: WidgetFAQ,
    },
    {
        type: RenderedServicesType.tabbedCardSliderV2,
        component: TabbedCardSliderV2,
    },
    {
        type: RenderedServicesType.tabbedCardSlider,
        predicate: hasTabbedServices,
        component: TabbedCardSliderV2,
    },
    {
        type: RenderedServicesType.tabbedCardSlider,
        component: TabbedCardSlider,
    },

    {
        type: RenderedServicesType.carouselV2,
        component: CarouselV2,
    },
    {
        type: RenderedServicesType.carousel,
        predicate: hasWholeBodyCarousel,
        component: CarouselV2,
    },
    {
        type: RenderedServicesType.carousel,
        component: StoreSkuCarousel,
    },

    {
        type: RenderedServicesType.featuredService,
        component: FeaturedService,
    },
    {
        type: RenderedServicesType.featuredCategoryRow,
        component: FeaturedCategoryRow,
    },
    {
        type: RenderedServicesType.categoryDirectory,
        component: CategoryDirectory,
    },
    {
        type: RenderedServicesType.shelfGroup,
        component: ShelfGroup,
    },
    {
        type: RenderedServicesType.testimonialReviewsV2,
        predicate: (service) => !!getTestimonialReviewsV2Data(service.id),
        component: WidgetTestimonialReviewsV2,
    },
    {
        type: RenderedServicesType.categoryHero,
        component: CategoryHero,
    },
    {
        type: RenderedServicesType.categoryTextHeader,
        component: CategoryPageTextHeader,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.insurance,
        component: InsuranceAccepted,
    },
    {
        type: RenderedServicesType.stack,
        component: StoreStack,
    },
    {
        type: RenderedServicesType.solo,
        component: SpecialistCard,
    },
    {
        type: RenderedServicesType.categoryGrid,
        component: StoreCategoryGrid,
    },
    {
        type: RenderedServicesType.adaptiveCarousel,
        component: StoreSkuCarousel,
    },

    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.support,
        dataKey: 'supportWidget',
        predicate: hasCategoryData('supportWidget'),
        component: SupportWidget,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.support,
        component: SupportWidget,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.generalAI,
        component: AISection,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.clinitians,
        component: ClinitianWidget,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.testimonial,
        component: TestimonialsWidget,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.blog,
        component: BlogSlider,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.categoryHeaderWithSearch,
        dataKey: 'categoryHeaderWithSearch',
        predicate: hasCategoryData('categoryHeaderWithSearch'),
        component: CategoryHeaderWithSearch,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.howItWorks,
        dataKey: 'howItWorks',
        predicate: hasCategoryData('howItWorks'),
        component: WidgetHowItWorks,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.categorySearch,
        dataKey: 'categorySearch',
        predicate: hasCategoryData('categorySearch'),
        component: CategorySearch,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.contentWithBulletList,
        dataKey: 'contentWithBulletList',
        predicate: hasCategoryData('contentWithBulletList'),
        component: CategoryContentWithBulletList,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.contentAccordion,
        dataKey: 'contentAccordion',
        predicate: hasCategoryData('contentAccordion'),
        component: WidgetContentAccordion,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.generalAISm,
        predicate: hasCategoryData('generalAISm'),
        component: WidgetGenAi,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.multiVideoPlayer,
        dataKey: 'multiVideoPlayer',
        predicate: hasCategoryData('multiVideoPlayer'),
        component: WidgetMultiVideoPlayer,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.testimonialUgc,
        dataKey: 'testimonialUgc',
        predicate: hasCategoryData('testimonialUgc'),
        component: WidgetTestimonialUGC,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.getStartedCta,
        dataKey: 'getStartedCta',
        predicate: hasCategoryData('getStartedCta'),
        component: WidgetGetStartedCta,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.faq,
        dataKey: 'faq',
        predicate: hasCategoryData('faq'),
        component: WidgetFAQ,
    },
    {
        type: RenderedServicesType.widget,
        cardType: ServiceItemCardType.categoryStats,
        dataKey: 'categoryStats',
        predicate: hasCategoryData('categoryStats'),
        component: WidgetCategoryStats,
    },
]

function normalizeServiceType(type: string | undefined): string | undefined {
    if (type === 'category_header') return RenderedServicesType.categoryHeader
    if (type === 'category_header_with_search') return RenderedServicesType.categoryHeaderWithSearch
    return type
}

export function findRegistryEntry(
    service: RenderedService,
    categoryName: string,
): RegistryEntry | null {
    const serviceType = normalizeServiceType(service.type as string | undefined)
    const registry = COMPONENT_REGISTRY

    for (const entry of registry) {
        if (entry.type !== serviceType) continue
        if (entry.type === RenderedServicesType.widget && entry.cardType !== service.cardType) continue
        if (entry.predicate && !entry.predicate(service, categoryName)) continue
        return entry
    }
    return null
}
